import React, { useState } from "react";
import { Header } from "../../Header";
import { retail_uat } from "../../Const";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const formatDateForApi = (date) => {
  if (!date) return "";
  return date.format('YYYY-MM-DD');
};

const formatDisplayDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

export const SyncReport = () => {
  const [fromDate, setFromDate] = useState(dayjs());
  const [currentStatus, setCurrentStatus] = useState('OPEN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncData, setSyncData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchText, setSearchText] = useState('');

  const token = localStorage.getItem('AuthorizationToken');

  const handleAuthError = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  const handleSearch = async () => {
    if (!fromDate) {
      setError('Please select a From Date.');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    const fetchByStatus = async (status) => {
      const params = new URLSearchParams();
      params.set('FromDate', formatDateForApi(fromDate));
      params.set('CurrentStatus', status);

      const response = await fetch(
        `${retail_uat}/api/v1/reportpendingpublishacceptance?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.status === 401) {
        handleAuthError();
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch sync report (${response.status})`);
      }

      const data = await response.json();
      const rows = Array.isArray(data) ? data : (data?.Result || data?.Data || data?.data || []);
      return Array.isArray(rows) ? rows : [];
    };

    try {
      if (currentStatus === 'BOTH') {
        const [openRows, closeRows] = await Promise.all([
          fetchByStatus('OPEN'),
          fetchByStatus('CLOS')
        ]);
        if (openRows === null || closeRows === null) return;
        setSyncData([...openRows, ...closeRows]);
      } else {
        const rows = await fetchByStatus(currentStatus);
        if (rows === null) return;
        setSyncData(rows);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch sync report.');
      setSyncData([]);
    } finally {
      setLoading(false);
    }
  };

  const matchesSearch = (row) => {
    const term = searchText.trim().toLowerCase();
    if (!term) return true;

    const fields = [row.PublishId, row.DocType, row.TransID]
      .map((v) => String(v ?? '').toLowerCase());

    return fields.some((v) => v.includes(term));
  };

  const filteredSyncData = syncData.filter(matchesSearch);

  const escapeCsvValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
  };

  const escapeCsvIdValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return `="${stringValue.replace(/"/g, '""')}"`;
  };

  const handleExportCsv = () => {
    const headers = [
      'Publish Date', 'Publish ID', 'Doc Type', 'Branch ID', 'Trans ID',
      'To Branch ID', 'Confirm Date', 'Delay Minutes'
    ];

    const csvRows = filteredSyncData.map((row) => ([
      escapeCsvValue(formatDisplayDateTime(row.PublishDate)),
      escapeCsvIdValue(row.PublishId),
      escapeCsvValue(row.DocType),
      escapeCsvIdValue(row.BranchID),
      escapeCsvIdValue(row.TransID),
      escapeCsvIdValue(row.ToBranchID),
      escapeCsvValue(row.ConfirmDate ? formatDisplayDateTime(row.ConfirmDate) : '-'),
      escapeCsvValue(row.DelayMinutes)
    ].join(',')));

    const csvContent = ['sep=,', headers.join(','), ...csvRows].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sync_report_${formatDateForApi(fromDate)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header />
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Sync Report</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              format="YYYY-MM-DD"
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="current-status-label">Status</InputLabel>
            <Select
              labelId="current-status-label"
              label="Status"
              value={currentStatus}
              onChange={(e) => setCurrentStatus(e.target.value)}
            >
              <MenuItem value="OPEN">OPEN</MenuItem>
              <MenuItem value="CLOS">CLOS</MenuItem>
              <MenuItem value="BOTH">BOTH</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Load Report'}
          </Button>

          <TextField
            size="small"
            placeholder="Search by publish ID/doc type/trans ID"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 320, flexGrow: 1, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={filteredSyncData.length === 0}
            sx={{ bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' } }}
          >
            Export CSV
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && hasSearched && !error && syncData.length === 0 && (
          <Alert severity="info">No pending sync records found for the selected filters.</Alert>
        )}

        {!loading && hasSearched && !error && syncData.length > 0 && filteredSyncData.length === 0 && (
          <Alert severity="info">No results match your search.</Alert>
        )}

        {filteredSyncData.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Publish Date</TableCell>
                    <TableCell>Publish ID</TableCell>
                    <TableCell>Doc Type</TableCell>
                    <TableCell>Branch ID</TableCell>
                    <TableCell>Trans ID</TableCell>
                    <TableCell>To Branch ID</TableCell>
                    <TableCell>Confirm Date</TableCell>
                    <TableCell align="right">Delay (mins)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSyncData.map((row, idx) => (
                    <TableRow key={`${row.PublishId}-${idx}`}>
                      <TableCell>{formatDisplayDateTime(row.PublishDate)}</TableCell>
                      <TableCell>{row.PublishId}</TableCell>
                      <TableCell>{row.DocType}</TableCell>
                      <TableCell>{row.BranchID}</TableCell>
                      <TableCell>{row.TransID}</TableCell>
                      <TableCell>{row.ToBranchID}</TableCell>
                      <TableCell>{row.ConfirmDate ? formatDisplayDateTime(row.ConfirmDate) : '-'}</TableCell>
                      <TableCell align="right">{row.DelayMinutes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </>
  );
};
